/**
 * The Company dossier — a detail route, not a nav entry.
 *
 * ## 🔴 The hero is the verdict, and the order of the page is the argument
 *
 * `API-and-UI.md` records this revision too: the valuation percentile was the hero in the first
 * version, which made the reader derive the conclusion from a statistic. The order is now
 * **verdict → the eight rules → what is against it → the evidence**, and every section below the
 * counter-case exists to let a suspicious number be traced back to the filing it came from.
 *
 * ⚠ **A `PRICE`-tier company's hero is the verdict too, and that verdict can never be `BUY`** —
 * R1 cannot be evaluated without accounts. The page states *why* (`no verdict · R1 unavailable at
 * PRICE tier`) rather than showing an empty badge, because an em dash would read as a failure to
 * load rather than as a fact about coverage.
 *
 * ## The dossier renders bear at equal weight, and that is a rule
 *
 * Bull and bear sit side by side in equal columns, bear **not** collapsed, with the refutation
 * pass appended as a distinct block rather than merged into either. Falsifiers and unknowns are
 * shown rather than hidden behind a disclosure: a claim you cannot state the falsifier for is not
 * a claim this system is allowed to publish. A rejected dossier shows *why* it was rejected — a
 * rejection is information about the pipeline, not an error to swallow.
 *
 * Every citation resolves to the **stored** document, never to the live URL. A dossier whose
 * evidence can 404 or be edited underneath it is a dossier that stops being reproducible, and the
 * whole scorecard rests on runs that can be reproduced.
 */

import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import { Num } from '../../design/components/data/Num.jsx';
import { MODE_LABELS, clockOf, useTimestampMode } from '../../ui/timestamps';
import { useIsPhone } from '../../ui/useIsPhone';
import { FamilyBars, RuleMark, TierMark, VerdictMark } from './glyphs';
import type { Company } from './model';

export function CompanyScreen({ company }: { company: Company }): JSX.Element {
  const mode = useTimestampMode();
  const phone = useIsPhone();
  const passed = company.rules.filter((rule) => rule.passed).length;

  return (
    <div className="jmt-screen jmt-stk-company">
      <div>
        <Link to="/screener" className="jmt-stk-back">
          <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor"
            strokeWidth="1.6" aria-hidden="true">
            <path d="M10 3.5L5.5 8l4.5 4.5" />
          </svg>
          Screener
        </Link>

        <p className="jmt-stk-company__ident">
          <code>
            {company.ticker} · {company.isin}
          </code>
          <span className="jmt-stk-company__name">{company.name}</span>
          <span className="jmt-stk-company__price">{company.price}</span>
          <span className="jmt-stk-company__asof">
            price at {clockOf(company.priceAsOf, mode)} {MODE_LABELS[mode]}
          </span>
        </p>

        {company.verdict ? (
          <p className="jmt-stk-hero">
            <VerdictMark verdict={company.verdict} size={34} />
            <span className="jmt-stk-hero__word">{company.verdict}</span>
          </p>
        ) : (
          // Not an em dash and not a blank badge. `no verdict` is a *state*, and the reason is
          // the whole content of it — see the module docstring.
          <p className="jmt-stk-hero jmt-stk-hero--none">
            <span className="jmt-stk-hero__word">no verdict</span>
          </p>
        )}

        <p className="jmt-stk-hero__sub">
          {company.verdict
            ? `${passed} of ${company.rules.length} rules pass`
            : company.noVerdictReason}{' '}
          · ruleset {company.rulesetVersion} · scored {clockOf(company.scoredAt, mode)}{' '}
          {MODE_LABELS[mode]}
        </p>
      </div>

      {/* 🔴 **Why, before how.** Looked at in the running app, this page went verdict → the eight
          rules → an EMPTY `AGAINST IT` → identity → a 24-row raw XBRL table, and the actual
          reasoning was at the very bottom under `AI dossier`. So the reader clicked a row that said
          "cheaper than 95% of its own EV/sales history" and landed on a page that made them scroll
          past two hundred lines to find out anything about the company.

          This block is the same three things the card leads with, in the same order, so the page
          opens by confirming the row rather than by restating the ruleset. The eight rules follow —
          they are the audit trail, and an audit trail belongs under the claim it supports. */}
      {company.highlights?.length || company.summary || company.families ? (
        <section className="jmt-panel jmt-stk-why-panel">
          <div className="jmt-stk-why-panel__head">
            <h2 className="jmt-stk-panel-label">WHY IT SCORES HERE</h2>
            {/* 🔴 The rating goes HERE rather than beside the hero verdict, and that placement is
                the design's own caution honoured rather than argued with: `API-and-UI.md` holds
                that the verdict is the claim and a number beside it invites precision the design
                refuses. Inside the panel that explains it, the number is the heading of an
                explanation instead of a qualifier on a verdict — and it is the same figure the row
                the reader just clicked was showing, which is the other half of why it must appear
                at all. A card that says 92 and a page that says nothing is a page that lost it. */}
            {company.score !== undefined ? (
              <span className="jmt-stk-score" title="rating out of 100, within this verdict group">
                <b>{company.score}</b>
                <span className="jmt-stk-score__of">/100</span>
              </span>
            ) : null}
          </div>
          {company.highlights?.length ? (
            <ul className="jmt-stk-why-list">
              {company.highlights.map((item) => (
                <li className="jmt-stk-why-list__item" key={item}>
                  {item}
                </li>
              ))}
            </ul>
          ) : null}
          {company.growth?.length ? (
            <p className="jmt-stk-growth jmt-stk-growth--page">
              <span className="jmt-stk-growth__label">REPORTED</span>
              {company.growth.map((item) => (
                <span className="jmt-stk-growth__item" key={item}>
                  {item}
                </span>
              ))}
            </p>
          ) : null}
          {company.families ? (
            <FamilyBars families={company.families} covered={company.covered} />
          ) : null}
          {company.summary ? <p className="jmt-stk-para">{company.summary}</p> : null}
        </section>
      ) : null}

      <section className="jmt-panel jmt-panel--flush">
        <h2 className="jmt-stk-panel-label">THE EIGHT RULES</h2>
        {company.rules.map((rule) => (
          <div className="jmt-stk-rule jmt-stk-rule--lg" key={rule.id}>
            <span className="jmt-stk-rule__id">{rule.id}</span>
            <span className="jmt-stk-rule__pair">
              <span className="jmt-stk-rule__name">{rule.name}</span>
              <span className="jmt-stk-rule__reading">{rule.reading}</span>
            </span>
            <span className="jmt-stk-rule__mark">
              <RuleMark passed={rule.passed} size={14} />
            </span>
          </div>
        ))}
      </section>

      {/* Directly under the rules, never behind a disclosure, and at body size rather than as a
          caption. The refutation pass is the reason the verdict above it is trustworthy. */}
      <section className="jmt-panel jmt-stk-against jmt-stk-against--page">
        <p className="jmt-stk-against__label">AGAINST IT</p>
        <p className="jmt-stk-against__text jmt-stk-against__text--lg">{company.against}</p>
      </section>

      <div className="jmt-stk-split">
        <section className="jmt-panel">
          <h2 className="jmt-panel__title">Identity and resolution</h2>
          <dl className="jmt-stk-pairs">
            <dt>ISIN</dt>
            <dd>
              <code>{company.isin}</code>
            </dd>
            <dt>CIK</dt>
            <dd>{company.identity.cik ? <code>{company.identity.cik}</code> : <Num value={null} />}</dd>
            <dt>Method</dt>
            <dd>{company.identity.method}</dd>
            <dt>Confidence</dt>
            <dd>{company.identity.confidence}</dd>
            <dt>Listings</dt>
            <dd>
              {company.identity.listings.map((listing) => (
                <span className="jmt-stk-listing" key={listing.line}>
                  {listing.line}
                  {listing.primary ? <span data-quiet> (primary)</span> : null}
                </span>
              ))}
            </dd>
            <dt>Coverage</dt>
            <dd>
              <TierMark tier={company.identity.tier} />
            </dd>
          </dl>
        </section>

        <section className="jmt-panel">
          <h2 className="jmt-panel__title">What changed</h2>
          <dl className="jmt-stk-pairs jmt-stk-pairs--dated">
            {(company.changes ?? []).map((change) => (
              <div key={change.on} className="jmt-stk-pairs__row">
                <dt>
                  <code>{change.on}</code>
                </dt>
                <dd>{change.what}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      <section className="jmt-panel jmt-panel--flush">
        <div className="jmt-stk-panel-head">
          <h2 className="jmt-panel__title">Fundamentals</h2>
          {/* 🔴 The filing date is a column, not a footnote. A fundamental without the date it
              became knowable cannot be used point-in-time, and a screener that scores on
              restated figures is scoring on information it did not have. */}
          <span className="jmt-stk-panel-label">EVERY VALUE CARRIES ITS FILING DATE</span>
        </div>
        {/* ⚠ **Three columns become two lines on a phone**, rather than a 136px `METRIC` column
            wrapping `Shares outstanding` onto two lines beside a 110px date. The filing date keeps
            its own line and its own label instead of being dropped: a fundamental without the date
            it became knowable cannot be used point-in-time, so it is not the column to sacrifice
            when space runs out. */}
        {phone ? (
          company.fundamentals.map((row) => (
            <div className="jmt-stk-fund" key={row.metric} data-stale={row.stale ? '' : undefined}>
              <span className="jmt-stk-fund__k">{row.metric}</span>
              <span className="jmt-stk-fund__v">{row.value ?? <Num value={null} />}</span>
              <span className="jmt-stk-fund__d">
                {row.periodEnd ? (
                  <>
                    {row.basis === 'instant' ? 'at' : 'to'} <code>{row.periodEnd}</code> ·{' '}
                  </>
                ) : null}
                filed <code>{row.filed}</code>
              </span>
              {row.stale ? <StaleNote lagDays={row.lagDays} /> : null}
            </div>
          ))
        ) : (
          <div className="jmt-stk-table jmt-stk-table--fundamentals">
            <div className="jmt-stk-table__head">
              <span>METRIC</span>
              <span data-num>VALUE</span>
              {/* ⚠ `PERIOD`, not a second date column of the same kind. `FILED` says when we could
                  know it and `PERIOD` says what it measures, and the old table carried only the
                  first — which is how a `LongTermDebt` reported for 2016 sat unremarked beside a
                  2026 balance sheet, both looking equally current. */}
              <span data-num>PERIOD</span>
              <span data-num>FILED</span>
            </div>
            {company.fundamentals.map((row) => (
              <div
                className="jmt-stk-table__row"
                key={row.metric}
                data-stale={row.stale ? '' : undefined}
              >
                <span>
                  {row.metric}
                  {/* The XBRL tag, kept and demoted. This table is where a suspicious figure gets
                      traced to a filing, so the tag cannot simply go — but it is provenance, not
                      the label, and it was the label for far too long. */}
                  {row.concept ? <code className="jmt-stk-fund__tag">{row.concept}</code> : null}
                </span>
                <span data-num>{row.value ?? <Num value={null} />}</span>
                <span data-num>
                  {row.periodEnd ? (
                    <>
                      <span className="jmt-stk-fund__basis">
                        {row.basis === 'instant' ? 'at' : 'to'}
                      </span>{' '}
                      <code>{row.periodEnd}</code>
                    </>
                  ) : (
                    <Num value={null} />
                  )}
                  {row.stale ? <StaleNote lagDays={row.lagDays} /> : null}
                </span>
                <span data-num>
                  <code>{row.filed}</code>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="jmt-stk-split">
        <section className="jmt-panel">
          <h2 className="jmt-panel__title">Price and trend</h2>
          {company.price52w ? (
            <>
              <p className="jmt-stk-range__ends">
                <span>{company.price52w.low}</span>
                <span data-quiet>52-WEEK RANGE</span>
                <span>{company.price52w.high}</span>
              </p>
              {/* A range bar, not a sparkline. P5 forbids a decorative plot; this one carries its
                  own ends and its own reading, so the mark means something without the caption.
                  ⚠ Drawn only when `pctOfRange` is a number: null means the range is zero — one
                  bar, or a line that has not moved — and a bar at 0% would claim the close sits at
                  a 52-week low it does not have. */}
              {company.price52w.pctOfRange === null ? (
                <p className="jmt-stk-range__note">
                  No range to place the close in — the window holds a single price.
                </p>
              ) : (
                <div className="jmt-stk-range" role="img" aria-label={company.price52w.note}>
                  <span
                    className="jmt-stk-range__fill"
                    style={{ width: `${company.price52w.pctOfRange}%` }}
                  />
                  <span
                    className="jmt-stk-range__at"
                    style={{ left: `${company.price52w.pctOfRange}%` }}
                  />
                </div>
              )}
              <p className="jmt-stk-range__note">{company.price52w.note}</p>
            </>
          ) : (
            <p className="jmt-stk-range__note">
              No 52-week range — this company has no stored price series.
            </p>
          )}
          <dl className="jmt-stk-pairs">
            {(company.trend ?? []).map((item) => (
              <div key={item.label} className="jmt-stk-pairs__row">
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="jmt-panel">
          <h2 className="jmt-panel__title">Money flow</h2>
          <dl className="jmt-stk-pairs">
            {(company.flow ?? []).map((item) => (
              <div key={item.label} className="jmt-stk-pairs__row">
                <dt>{item.label}</dt>
                {/* `null` renders `Num`'s em dash in tertiary — *not knowable*, never zero. */}
                <dd>{item.value ?? <Num value={null} />}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      <section className="jmt-panel jmt-panel--flush">
        <div className="jmt-stk-panel-head">
          <h2 className="jmt-panel__title">Events</h2>
          <span className="jmt-stk-panel-label">DISCLOSURE LAG PRINTED</span>
        </div>
        {company.events.map((event) => (
          <div className="jmt-stk-event" key={event.citation}>
            <span className="jmt-stk-event__type">
              <code>{event.type}</code>
            </span>
            <span className="jmt-stk-event__what">
              {event.what} <a href="#stored-document">{event.citation}</a>
            </span>
            {/* 🔴 The lag, printed. A 13F is 45 days old the day it appears, and a flow signal
                read as *today* is the misreading this column exists to prevent. */}
            <span className="jmt-stk-event__lag">lag {event.lagDays}d</span>
          </div>
        ))}
      </section>

      <section>
        <div className="jmt-stk-panel-head jmt-stk-panel-head--section">
          <h2 className="jmt-stk-dossier__title">AI dossier</h2>
          <span className="jmt-stk-dossier__meta">
            as of {company.dossier.asOf} · {company.dossier.modelId} · prompt{' '}
            {company.dossier.promptVersion} · build + refute
          </span>
        </div>

        {company.dossier.rejectedReason ? (
          <div className="jmt-panel jmt-stk-rejected">
            <p className="jmt-stk-against__label">REJECTED — NOT PUBLISHED</p>
            <p className="jmt-stk-against__text">{company.dossier.rejectedReason}</p>
          </div>
        ) : null}

        <div className="jmt-stk-split">
          <section className="jmt-panel">
            <p className="jmt-stk-panel-label">BULL</p>
            <Claims claims={company.dossier.bull} />
          </section>
          {/* Equal column, equal weight, not collapsed. */}
          <section className="jmt-panel">
            <p className="jmt-stk-panel-label">BEAR</p>
            <Claims claims={company.dossier.bear} />
          </section>
        </div>

        <section className="jmt-panel jmt-stk-refutation">
          <p className="jmt-stk-panel-label">REFUTATION PASS</p>
          <p className="jmt-stk-prose">{company.dossier.refutation}</p>
        </section>

        <div className="jmt-stk-split">
          <section className="jmt-panel">
            <p className="jmt-stk-panel-label">FALSIFIERS</p>
            <ul className="jmt-stk-list">
              {company.dossier.falsifiers.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
          <section className="jmt-panel">
            <p className="jmt-stk-panel-label">UNKNOWNS</p>
            <ul className="jmt-stk-list">
              {company.dossier.unknowns.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        </div>
      </section>

    </div>
  );
}

function Claims({ claims }: { claims: { text: string; citation: string }[] }): JSX.Element {
  return (
    <div className="jmt-stk-claims">
      {claims.map((claim) => (
        <p key={claim.citation + claim.text.slice(0, 12)}>
          {claim.text} <a href="#stored-document">{claim.citation}</a>
        </p>
      ))}
    </div>
  );
}


/**
 * The marker on a line the issuer stopped filing.
 *
 * 🔴 **Measured against the company's own newest period, never against today.** A filer that is
 * merely late reports everything late; what this catches is one line frozen while the rest moved
 * on — Texas Roadhouse's `LongTermDebt` last filed **2016-08-05** beside a 2026-08-07 balance
 * sheet, rendered identically to it.
 *
 * ⚠ **Not a colour.** `--attn` means money at risk and this is a data-coverage fact, so the marker
 * is a word and a dotted rule. The row also dims, which is the honest visual claim: this figure is
 * still true of the period it names, and is no longer true of the company.
 */
function StaleNote({ lagDays }: { lagDays?: number }): JSX.Element {
  const years = lagDays ? Math.floor(lagDays / 365) : 0;
  return (
    <span
      className="jmt-stk-fund__stale"
      title={
        lagDays
          ? `${lagDays} days behind this company's newest reported period`
          : 'behind this company\'s newest reported period'
      }
    >
      stale{years >= 1 ? ` · ${years}y+` : ''}
    </span>
  );
}
